import React, { useEffect } from 'react'
import { WebAppProvider, type Options } from '@vkruglikov/react-telegram-web-app'
import { useAuth } from './store'
import { initTelegramWebAppIfNeeded } from '../utils/telegramLoader'
import { logger } from '../utils/logger'

// Типы для Telegram WebApp API
declare global {
  interface Window {
    Telegram?: {
      WebApp: any;
    };
  }
}

interface TelegramProviderProps {
  children: React.ReactNode
  options?: Options
}

/**
 * Провайдер для интеграции с Telegram WebApp
 * 
 * Автоматически определяет режим работы и предоставляет соответствующий контекст:
 * - В Telegram: WebAppProvider с полным API
 * - В браузере: простой React.Fragment без Telegram API
 */
export const TelegramProvider: React.FC<TelegramProviderProps> = ({ 
  children, 
  options 
}) => {
  const { mode, isTelegramMode } = useAuth()

  // Настройки по умолчанию для Telegram WebApp
  const defaultOptions: Options = {
    smoothButtonsTransition: true,
    ...options
  }

  useEffect(() => {
    // Инициализация Telegram WebApp API при первой загрузке
    if (isTelegramMode) {
      logger.log('🔄 ДИАГНОСТИКА: Инициализируем Telegram WebApp API в TelegramProvider')
      initTelegramWebAppIfNeeded().then((initialized) => {
        logger.log('📱 ДИАГНОСТИКА: TelegramProvider - API инициализирован:', initialized)
        if (initialized && window.Telegram?.WebApp) {
          try {
            const webapp = window.Telegram.WebApp
            
            logger.log('📱 ДИАГНОСТИКА: WebApp объект доступен', {
              version: webapp.version,
              platform: webapp.platform,
              hasInitData: !!webapp.initData,
              initDataLength: webapp.initData?.length || 0,
              userId: webapp.initDataUnsafe?.user?.id
            })
            
            // Настройка цветовой схемы
            if (webapp.colorScheme === 'dark') {
              document.documentElement.setAttribute('data-theme', 'dark')
            } else {
              document.documentElement.setAttribute('data-theme', 'light')
            }
            
            // Слушаем изменения темы
            webapp.onEvent('themeChanged', () => {
              if (webapp.colorScheme === 'dark') {
                document.documentElement.setAttribute('data-theme', 'dark')
              } else {
                document.documentElement.setAttribute('data-theme', 'light')
              }
            })
          } catch (error) {
            logger.warn('⚠️ Ошибка при настройке Telegram WebApp темы:', error)
          }
        } else {
          logger.warn('⚠️ ДИАГНОСТИКА: Telegram WebApp API недоступен после инициализации')
        }
      }).catch((error) => {
        logger.error('❌ Ошибка при инициализации Telegram WebApp:', error)
      })
    }
  }, [isTelegramMode])

  // Для режима Telegram используем WebAppProvider
  if (isTelegramMode) {
    return (
      <WebAppProvider options={defaultOptions}>
        {children}
      </WebAppProvider>
    )
  }

  // Для браузерного режима возвращаем обычный контейнер
  return <>{children}</>
}

/**
 * Хук для доступа к Telegram WebApp API
 * Безопасно работает в обоих режимах
 */
export const useTelegramWebApp = () => {
  const { isTelegramMode } = useAuth()
  
  // В браузерном режиме возвращаем заглушки
  if (!isTelegramMode) {
    return {
      webApp: null,
      user: null,
      isAvailable: false,
      initDataUnsafe: null,
      initData: null,
      colorScheme: 'light' as const,
      themeParams: {},
      version: null,
      platform: null,
      showPopup: () => Promise.resolve(''),
      showAlert: () => Promise.resolve(),
      showConfirm: () => Promise.resolve(false),
      hapticFeedback: {
        impactOccurred: () => {},
        notificationOccurred: () => {},
        selectionChanged: () => {}
      }
    }
  }
  
  // В Telegram режиме используем реальный API
  const webApp = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  
  return {
    webApp,
    user: webApp?.initDataUnsafe?.user || null,
    isAvailable: !!webApp,
    initDataUnsafe: webApp?.initDataUnsafe || null,
    initData: webApp?.initData || null,
    colorScheme: webApp?.colorScheme || 'light',
    themeParams: webApp?.themeParams || {},
    version: webApp?.version || null,
    platform: webApp?.platform || null,
    
    // Функции для показа диалогов
    showPopup: (params: { message: string; title?: string }) => {
      if (!webApp) return Promise.resolve('')
      return new Promise<string>((resolve) => {
        webApp.showPopup(params, resolve)
      })
    },
    
    showAlert: (message: string) => {
      if (!webApp) return Promise.resolve()
      return new Promise<void>((resolve) => {
        webApp.showAlert(message, resolve)
      })
    },
    
    showConfirm: (message: string) => {
      if (!webApp) return Promise.resolve(false)
      return new Promise<boolean>((resolve) => {
        webApp.showConfirm(message, resolve)
      })
    },
    
    // Haptic feedback
    hapticFeedback: {
      impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
        webApp?.HapticFeedback?.impactOccurred(style)
      },
      notificationOccurred: (type: 'error' | 'success' | 'warning') => {
        webApp?.HapticFeedback?.notificationOccurred(type)
      },
      selectionChanged: () => {
        webApp?.HapticFeedback?.selectionChanged()
      }
    }
  }
}
